import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormGroup, Label, Input, FormFeedback, Spinner } from 'reactstrap';
import Select from 'react-select';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';
import { Check } from 'react-feather';
import { getBrandById } from '../../ReduxToolkit/Reducers/brands';
import { market } from '../../static/brands_wholesalers_data';

const marketOptions = market
  .filter((m) => m.label !== 'Open')
  .map((m) => ({
    value: m.id,
    label: m.label.charAt(0).toUpperCase() + m.label.slice(1),
    code: m.code,
  }));

const Step1BrandInfo = ({ control, errors }) => {
  const dispatch = useDispatch();
  const { setValue, watch, clearErrors } = useFormContext();
  const [fetchState, setFetchState] = useState({ loading: false, error: null, loaded: false });
  const brandId = watch('brand_info.brand_id');
  const brandName = watch('brand_info.brand_name');

  const fetchBrand = useCallback(async (id) => {
    const numericId = Number(id);

    if (!id || !Number.isInteger(numericId) || numericId <= 0) {
      setFetchState({ loading: false, error: null, loaded: false });
      return;
    }

    setFetchState({ loading: true, error: null, loaded: false });

    const result = await dispatch(getBrandById(numericId));

    if (result?.statusCode === 200 && result?.data) {
      setValue('brand_info.brand_name', result.data.name || '', { shouldValidate: true });
      setValue('brand_info.website', result.data.website || '', { shouldValidate: true });
      clearErrors(['brand_info.brand_name', 'brand_info.website', 'brand_info.brand_id']);
      setFetchState({ loading: false, error: null, loaded: true });
    } else {
      setFetchState({
        loading: false,
        error: result?.message || 'Brand not found',
        loaded: false,
      });
    }
  }, [dispatch, setValue, clearErrors]);

  useEffect(() => {
    const handler = debounce((id) => {
      fetchBrand(id);
    }, 500);

    handler(brandId);
    return () => handler.cancel();
  }, [brandId, fetchBrand]);

  const hasPopulatedBrand = fetchState.loaded && brandName;

  return (
    <div style={{
      maxWidth: '570px',
      width: '100%',
      background: '#fff',
      borderRadius: '10px',
      padding: '32px 36px',
    }}>
      <h5 style={{ fontWeight: '600', fontSize: '18px', marginBottom: '4px' }}>Brand Info</h5>
      <p style={{ color: '#888', fontSize: '13px', marginBottom: '28px' }}>
        Add basic brand info for your request, or enter a brand ID to auto-fill name and website
      </p>

      <FormGroup>
        <Label style={labelStyle}>
          Brand ID{' '}
          <span style={{ color: '#aaa', fontWeight: '400' }}>(optional)</span>
        </Label>
        <div style={{ position: 'relative' }}>
          <Controller
            name="brand_info.brand_id"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="number"
                min="1"
                placeholder="Enter brand ID to auto-fill"
                invalid={!!errors?.brand_info?.brand_id || !!fetchState.error}
                style={{
                  ...inputStyle,
                  paddingRight: fetchState.loading || hasPopulatedBrand ? '40px' : '12px',
                }}
                onChange={(e) => {
                  field.onChange(e);
                  setFetchState({ loading: false, error: null, loaded: false });
                }}
              />
            )}
          />
          {fetchState.loading && (
            <div style={inputIconStyle}>
              <Spinner size="sm" color="secondary" />
            </div>
          )}
          {!fetchState.loading && hasPopulatedBrand && (
            <div style={inputIconStyle}>
              <Check size={16} color="#28a745" />
            </div>
          )}
        </div>
        <FormFeedback>{errors?.brand_info?.brand_id?.message || fetchState.error}</FormFeedback>
      </FormGroup>

      <FormGroup>
        <Label style={labelStyle}>
          Brand Name <span style={{ color: '#e53' }}>*</span>
        </Label>
        <Controller
          name="brand_info.brand_name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              placeholder="Enter brand name"
              invalid={!!errors?.brand_info?.brand_name}
              style={inputStyle}
              onChange={(e) => {
                field.onChange(e);
                setFetchState((prev) => ({ ...prev, loaded: false }));
              }}
            />
          )}
        />
        <FormFeedback>{errors?.brand_info?.brand_name?.message}</FormFeedback>
      </FormGroup>

      <FormGroup>
        <Label style={labelStyle}>
          Brand Website URL <span style={{ color: '#e53' }}>*</span>
        </Label>
        <Controller
          name="brand_info.website"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="text"
              placeholder="http://example.com"
              invalid={!!errors?.brand_info?.website}
              style={inputStyle}
              onChange={(e) => {
                field.onChange(e);
                setFetchState((prev) => ({ ...prev, loaded: false }));
              }}
            />
          )}
        />
        <FormFeedback>{errors?.brand_info?.website?.message}</FormFeedback>
      </FormGroup>

      <FormGroup>
        <Label style={labelStyle}>
          Deadline{' '}
          <span style={{ color: '#aaa', fontWeight: '400' }}>(optional)</span>
        </Label>
        <Controller
          name="brand_info.deadline"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="date"
              style={inputStyle}
            />
          )}
        />
      </FormGroup>

      <FormGroup className="mb-0">
        <Label style={labelStyle}>
          Markets of Interest{' '}
          <span style={{ color: '#aaa', fontWeight: '400' }}>(optional)</span>
        </Label>
        <Controller
          name="brand_info.markets_of_interest"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              isMulti
              options={marketOptions}
              value={value || []}
              onChange={onChange}
              placeholder="Search and select markets..."
              classNamePrefix="br-select"
              styles={selectStyles}
            />
          )}
        />
        {errors?.brand_info?.markets_of_interest && (
          <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
            {errors.brand_info.markets_of_interest.message}
          </div>
        )}
      </FormGroup>
    </div>
  );
};

const labelStyle = {
  fontSize: '13px',
  fontWeight: '500',
  color: '#333',
  marginBottom: '6px',
};

const inputStyle = {
  fontSize: '13px',
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '10px 12px',
  color: '#333',
};

const inputIconStyle = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    fontSize: '13px',
    borderRadius: '6px',
    borderColor: state.isFocused ? '#888' : '#d9d9d9',
    boxShadow: 'none',
    '&:hover': { borderColor: '#888' },
    minHeight: '42px',
  }),
  option: (base) => ({
    ...base,
    fontSize: '13px',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#f0f0f0',
    borderRadius: '4px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: '12px',
    color: '#333',
  }),
  multiValueRemove: (base) => ({
    ...base,
    '&:hover': { backgroundColor: '#ddd', color: '#333' },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#aaa',
    fontSize: '13px',
  }),
};

export default Step1BrandInfo;
